import type {ReactElement} from 'react';
import React, {useEffect, useLayoutEffect, useRef, useState} from 'react';
import {motion} from 'framer-motion';
import {Text, TextSize} from '@Components/text';
import {Swatches} from '@Components/color-picker-v2/components/swatches';
import {Button, IconPlacement, Size, Type} from '@Components/button';
import styles from './on-my-design-section.module.scss';
import type {RGBA} from '@Utils/color.util';
import {hexToRgba} from '@Utils/color.util';

interface OnMyDesignSectionProps {
  className?: string;
  colorsOnDesign: string[];

  onColorSelect(color: RGBA | string): void;

  isMobileVersion?: boolean;

  onClickAdd?(): void;

  isAlphaDisabled?: boolean;
}

const MAX_VISIBLE_ROWS = 4;

export function OnMyDesignSection({
  isAlphaDisabled = false,
  className = '',
  onClickAdd = (): void => {},
  isMobileVersion = false,
  ...props
}: OnMyDesignSectionProps): ReactElement<any> | null {
  const [isExpanded, setIsExpanded] = useState<boolean>(false);
  const [collapsedHeight, setCollapsedHeight] = useState<number | null>(null);
  const [fullHeight, setFullHeight] = useState<number>(0);
  const contentRef = useRef<HTMLDivElement>(null);
  const hasOverflow = collapsedHeight !== null;

  const measureHeights = (): void => {
    const contentEl = contentRef.current;
    if (!contentEl) {
      return;
    }
    setFullHeight(contentEl.scrollHeight);

    const contentTop = contentEl.getBoundingClientRect().top;
    const swatchContainer = contentEl.firstElementChild as HTMLElement | null;
    const swatches = swatchContainer ? (Array.from(swatchContainer.children) as HTMLElement[]) : [];
    const rowTops: number[] = [];
    for (const swatch of swatches) {
      const top = Math.round(swatch.getBoundingClientRect().top - contentTop);
      if (!rowTops.includes(top)) {
        rowTops.push(top);
      }
      if (rowTops.length > MAX_VISIBLE_ROWS) {
        break;
      }
    }

    if (rowTops.length <= MAX_VISIBLE_ROWS) {
      setCollapsedHeight(null);
    } else {
      setCollapsedHeight(rowTops[MAX_VISIBLE_ROWS]);
    }
  };

  useLayoutEffect((): void => {
    measureHeights();
  }, [props.colorsOnDesign, isMobileVersion]);

  useEffect((): (() => void) | undefined => {
    const contentEl = contentRef.current;
    if (!contentEl) {
      return undefined;
    }
    const observer = new ResizeObserver(measureHeights);
    observer.observe(contentEl);
    return (): void => {
      observer.disconnect();
    };
  }, []);

  const animatedHeight = hasOverflow && !isExpanded ? collapsedHeight : fullHeight;

  const getViewToggleButton = (): ReactElement<any> | null => {
    if (!hasOverflow) {
      return null;
    }

    return (
      <div className="_full-width flex-center">
        <Button
          size={Size.XSMALL}
          type={Type.GHOST}
          text={isExpanded ? window.i18next.t('pmwjs_view_less') : window.i18next.t('pmwjs_view_all')}
          icon={isExpanded ? 'icon-caret-up' : 'icon-caret-down'}
          iconPlacement={IconPlacement.RIGHT}
          onClick={(): void => {
            setIsExpanded(!isExpanded);
          }}
        />
      </div>
    );
  };

  return (
    <div className={`flex-v-row ${isMobileVersion ? 'spacing-m-t-0' : 'spacing-m-t-6'} ${className}`}>
      <Text
        size={isMobileVersion ? TextSize.XXSMALL : TextSize.XSMALL}
        bold
        val={window.i18next.t('pmwjs_on_my_design')}
        className={`${isMobileVersion ? '' : styles.onMyDesignHeading} content-body`}
      />
      <motion.div
        initial={false}
        animate={{height: animatedHeight}}
        transition={{duration: 0.25, ease: 'easeInOut'}}
        className={styles.swatchesWrapper}
      >
        <div ref={contentRef} className={styles.swatchesContent}>
          <Swatches
            onClick={(color: string): void => {
              props.onColorSelect(isAlphaDisabled ? color : hexToRgba(color));
            }}
            colors={props.colorsOnDesign}
            showAddButton={isMobileVersion}
            onClickAdd={onClickAdd}
            hasCustomMargins={isMobileVersion}
            className={isMobileVersion ? 'spacing-m-t-2' : ''}
          />
        </div>
      </motion.div>
      {getViewToggleButton()}
    </div>
  );
}
